/**
 * i18n — single entry point for UI language (react-i18next).
 *
 * Conventions (full doc: docs/implementations/group/I18N_CONVENTIONS.md)
 *   - One JSON file per language: ./locales/<lang>.json. French is the reference.
 *   - Keys are `ns.section.key` (e.g. `common.actions.save`, `nav.vehicles`).
 *     The first segment is a flat "namespace" = top-level object of the JSON.
 *     There is NO i18next namespace separator: `t('common.actions.save')` just works.
 *   - Detection order: localStorage.fm_lang > user.language > organization.language > 'fr'.
 *     Only the first step is known at boot; the two others are applied by
 *     `applyLanguagePreference()` once the session is loaded (see LanguageSync in Layout).
 *   - Missing key in `en` → falls back to `fr`. Missing key everywhere → the key itself,
 *     which is visible on screen on purpose (a bare `vehicles.list.title` is a bug report).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import fr from './locales/fr.json';
import en from './locales/en.json';

export const DEFAULT_LANGUAGE = 'fr';
export const LANG_STORAGE_KEY = 'fm_lang';

/** Languages offered in the header switcher. Adding a language = one JSON + one line here. */
export const SUPPORTED_LANGUAGES = [
  { code: 'fr', label: 'Français', short: 'FR', locale: 'fr-FR' },
  { code: 'en', label: 'English', short: 'EN', locale: 'en-GB' },
];

const RESOURCES = {
  fr: { translation: fr },
  en: { translation: en },
};

function isSupported(code) {
  return !!code && SUPPORTED_LANGUAGES.some((l) => l.code === code);
}

/** Normalise 'en-US', 'EN', 'en_GB' → 'en'. Returns null when unknown. */
export function normalizeLanguage(code) {
  if (!code || typeof code !== 'string') return null;
  const short = code.trim().toLowerCase().split(/[-_]/)[0];
  return isSupported(short) ? short : null;
}

function readStoredLanguage() {
  try {
    return normalizeLanguage(window.localStorage.getItem(LANG_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Language known at boot: explicit user choice on this browser, else French. */
export function detectInitialLanguage() {
  return readStoredLanguage() || DEFAULT_LANGUAGE;
}

/** BCP-47 locale for Intl (dates, numbers) matching the active UI language. */
export function currentLocale() {
  const code = normalizeLanguage(i18n.language) || DEFAULT_LANGUAGE;
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.locale || 'fr-FR';
}

/**
 * Switch the UI language.
 * @param {string} code  'fr' | 'en'
 * @param {Object} [options]
 * @param {boolean} [options.persist=true]  remember the choice in localStorage.fm_lang
 */
export async function setLanguage(code, { persist = true } = {}) {
  const lang = normalizeLanguage(code) || DEFAULT_LANGUAGE;
  if (persist) {
    try { window.localStorage.setItem(LANG_STORAGE_KEY, lang); } catch { /* private mode */ }
  }
  if (i18n.language !== lang) await i18n.changeLanguage(lang);
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
  return lang;
}

/**
 * Apply the server-side preference once the session is known:
 * user.language, then organization.language. An explicit browser choice
 * (localStorage.fm_lang) always wins and is left untouched.
 */
export function applyLanguagePreference({ user, organization } = {}) {
  if (readStoredLanguage()) return i18n.language;
  const preferred = normalizeLanguage(user?.language) || normalizeLanguage(organization?.language);
  if (preferred && preferred !== i18n.language) {
    setLanguage(preferred, { persist: false });
  }
  return preferred || i18n.language;
}

i18n
  .use(initReactI18next)
  .init({
    resources: RESOURCES,
    lng: detectInitialLanguage(),
    // Resources are bundled: initialise synchronously so the very first render (login page)
    // is already in localStorage.fm_lang — the default defers init to a setTimeout, which
    // paints one FR frame before switching to EN (flash).
    initImmediate: false,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    defaultNS: 'translation',
    // Keys are dotted paths inside one big object — no namespace prefix syntax.
    nsSeparator: false,
    keySeparator: '.',
    returnEmptyString: false,
    interpolation: { escapeValue: false }, // React already escapes
    react: { useSuspense: false },
  });

if (typeof document !== 'undefined') document.documentElement.lang = i18n.language;

export default i18n;
